import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
// Icons removed - text-only UI
import { calculateFeeReduction } from "./LegalTeamSelection";
import { debug } from '../../lib/debug.js';

// ============================================================================
// SLOT NEGOTIATION COMPONENT
// ============================================================================

export default function SlotNegotiation({
  airportCode,
  airportName,
  availableSlots = [],
  competitors = [],
  playerSchedule = [],
  hubCode = "PDX",
  worldState,
  onBidSubmit,
  onDecline,
  onGoBack,
}) {
  debug("[SlotNegotiation] Rendering with props:", {
    airportCode,
    airportName,
    availableSlots,
    competitors,
    hubCode,
    worldState: worldState ? "present" : "null",
  });

  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [bidAmount, setBidAmount] = useState(50000);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showConflictCheck, setShowConflictCheck] = useState({});

  // Get legal info
  const legalState = worldState?.legal;
  const currentFirm = legalState?.currentFirm;
  const feeReduction = calculateFeeReduction(legalState, airportCode);
  const playerCash = worldState?.airline?.cash || 0;

  // Base costs
  const BASE_COSTS = {
    facilityFee: 50000,      // One-time per slot
    monthlyGateLease: 30000, // Per slot per month
    perTurnFee: 200,         // Per departure/arrival
    landingFee: 3,           // Per 1,000 lbs
  };

  // Apply legal discount to gate lease
  const discountedGateLease = Math.round(BASE_COSTS.monthlyGateLease * (1 - feeReduction));
  const monthlySavings = BASE_COSTS.monthlyGateLease - discountedGateLease;

  // Calculate total costs
  const calculateTotalCost = () => {
    if (!selectedQuantity) return null;

    const facilityTotal = BASE_COSTS.facilityFee * selectedQuantity;
    const bidTotal = bidAmount * selectedQuantity;
    const firstMonthLease = discountedGateLease * selectedQuantity;

    return {
      facilityFee: facilityTotal,
      bidAmount: bidTotal,
      firstMonthLease,
      totalUpfront: facilityTotal + bidTotal + firstMonthLease,
      monthlySavings: monthlySavings * selectedQuantity,
    };
  };

  const totalCost = calculateTotalCost();
  const canAfford = totalCost ? playerCash >= totalCost.totalUpfront : true;

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  // Format time (with null safety)
  const formatTime = (time) => {
    if (!time || typeof time !== 'string') return "N/A";
    const parts = time.split(":");
    if (parts.length < 2) return time;
    const [hours, minutes] = parts;
    const h = parseInt(hours);
    if (isNaN(h)) return time;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Check for schedule conflicts
  const checkConflicts = (slot) => {
    const conflicts = [];

    // Safety check for slot data
    if (!slot || !slot.departTime || !slot.returnTime) {
      return conflicts;
    }

    // Parse slot times (with safety)
    const slotDepart = parseInt((slot.departTime || "").replace(":", "") || "0");
    const slotReturn = parseInt((slot.returnTime || "").replace(":", "") || "0");
    const hubDepart = parseInt((slot.hubDepartureRequired || "").replace(":", "") || "0");
    const hubReturn = parseInt((slot.hubReturnTime || "").replace(":", "") || "0");

    // Check against player's existing schedule
    for (const flight of playerSchedule) {
      const flightDepart = parseInt(flight.departureTime?.replace(":", "") || "0");
      const flightArrive = parseInt(flight.arrivalTime?.replace(":", "") || "0");

      // Check if this flight uses the same aircraft and conflicts with hub departure
      if (flight.origin === hubCode) {
        // Departing from hub - check against required hub departure
        if (Math.abs(flightDepart - hubDepart) < 100) { // Within ~1 hour
          conflicts.push({
            type: "departure",
            flight: flight,
            message: `Conflicts with ${flight.flightNumber} departing ${hubCode} at ${formatTime(flight.departureTime)}`,
          });
        }
      }

      if (flight.destination === hubCode) {
        // Arriving at hub - check against required hub return
        if (Math.abs(flightArrive - hubReturn) < 100) {
          conflicts.push({
            type: "arrival",
            flight: flight,
            message: `Conflicts with ${flight.flightNumber} arriving ${hubCode} at ${formatTime(flight.arrivalTime)}`,
          });
        }
      }
    }

    return conflicts;
  };

  // Toggle slot selection
  const toggleSlotSelection = (slotId) => {
    setSelectedSlots(prev => {
      if (prev.includes(slotId)) {
        return prev.filter(id => id !== slotId);
      }
      if (selectedQuantity && prev.length >= selectedQuantity) {
        return prev; // Can't select more than quantity
      }
      return [...prev, slotId];
    });
  };

  // Handle bid submission
  const handleSubmit = () => {
    if (!selectedQuantity || selectedSlots.length !== selectedQuantity) return;
    if (bidAmount < 50000) return;
    if (!canAfford) return;

    onBidSubmit({
      airportCode,
      slots: selectedSlots,
      bidPerSlot: bidAmount,
      totalBid: bidAmount * selectedQuantity,
      totalCost: totalCost.totalUpfront,
      legalFirm: currentFirm?.id || null,
      feeReduction,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-5xl w-full max-h-[95vh] overflow-y-auto">

        {/* ================================================================ */}
        {/* HEADER SECTION */}
        {/* ================================================================ */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div>
                <h2 className="text-xl font-bold text-white">{airportName || "Airport Authority"}</h2>
                <p className="text-gray-400">Slot Negotiation - {airportCode}</p>
              </div>
            </div>
            <Badge className={currentFirm ? "bg-green-600" : "bg-red-600"}>
              {currentFirm ? currentFirm.name : "UNREPRESENTED"}
            </Badge>
          </div>

          {/* Flavor Text */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-200 italic">
              "Congratulations on your growing operation. We have slots available and would like to invite you to negotiate.
              {airportCode} is committed to fostering healthy competition and welcomes new carriers who can demonstrate reliable service."
            </p>
          </div>

          {/* Legal Status */}
          {currentFirm && feeReduction > 0 && (
            <div className="mt-3 text-sm text-green-400">
              {currentFirm.name} has secured a {Math.round(feeReduction * 100)}% discount on gate lease fees
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* COSTS BREAKDOWN */}
        {/* ================================================================ */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-4">
            $ Cost Structure (Transparent - No Hidden Fees)
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between p-2 bg-gray-800 rounded">
                <span className="text-gray-300">One-time Facility Fee</span>
                <span className="text-white font-medium">{formatCurrency(BASE_COSTS.facilityFee)} per slot</span>
              </div>
              <div className="flex justify-between p-2 bg-gray-800 rounded">
                <div className="flex items-center gap-2">
                  <span className="text-gray-300">Monthly Gate Lease</span>
                  {feeReduction > 0 && (
                    <Badge variant="outline" className="text-green-400 border-green-400 text-xs">
                      -{Math.round(feeReduction * 100)}%
                    </Badge>
                  )}
                </div>
                <div className="text-right">
                  {feeReduction > 0 ? (
                    <>
                      <span className="text-gray-500 line-through mr-2">{formatCurrency(BASE_COSTS.monthlyGateLease)}</span>
                      <span className="text-green-400 font-medium">{formatCurrency(discountedGateLease)} per slot</span>
                    </>
                  ) : (
                    <span className="text-white font-medium">{formatCurrency(BASE_COSTS.monthlyGateLease)} per slot</span>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between p-2 bg-gray-800 rounded">
                <span className="text-gray-300">Per-Turn Fee</span>
                <span className="text-white font-medium">{formatCurrency(BASE_COSTS.perTurnFee)} per departure/arrival</span>
              </div>
              <div className="flex justify-between p-2 bg-gray-800 rounded">
                <span className="text-gray-300">Landing Fee</span>
                <span className="text-white font-medium">${BASE_COSTS.landingFee} per 1,000 lbs</span>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* TERMS */}
        {/* ================================================================ */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-4">
            Terms & Conditions
          </h3>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-3 bg-gray-800 rounded-lg">
              <div className="mb-1">
                <span className="text-amber-400 font-medium">Minimum Commitment</span>
              </div>
              <p className="text-white text-lg font-bold">6 months</p>
              <p className="text-gray-500 text-xs">No early termination refunds</p>
            </div>
            <div className="p-3 bg-gray-800 rounded-lg">
              <div className="mb-1">
                <span className="text-blue-400 font-medium">First Flight Required</span>
              </div>
              <p className="text-white text-lg font-bold">Within 14 days</p>
              <p className="text-gray-500 text-xs">Of slot purchase</p>
            </div>
            <div className="p-3 bg-gray-800 rounded-lg">
              <div className="mb-1">
                <span className="text-red-400 font-medium">Ongoing Requirement</span>
              </div>
              <p className="text-white text-lg font-bold">1 flight per 7 days</p>
              <p className="text-gray-500 text-xs">Or slot revoked (no refund)</p>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* AVAILABLE SLOTS */}
        {/* ================================================================ */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-4">
            Available Slots
          </h3>

          <div className="space-y-3">
            {(availableSlots || []).filter(slot => slot && slot.id).map((slot) => {
              const conflicts = checkConflicts(slot);
              const isSelected = selectedSlots.includes(slot.id);
              const showConflicts = showConflictCheck[slot.id];

              return (
                <Card
                  key={slot.id}
                  className={`bg-gray-800 border-2 transition-all cursor-pointer ${
                    isSelected
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                  onClick={() => selectedQuantity && toggleSlotSelection(slot.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Slot Header */}
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className="bg-purple-600 text-white font-bold">
                            {slot.id}
                          </Badge>
                          {isSelected && (
                            <Badge className="bg-blue-600">SELECTED</Badge>
                          )}
                          {conflicts.length > 0 && (
                            <Badge variant="destructive">
                              ! {conflicts.length} Conflict{conflicts.length > 1 ? "s" : ""}
                            </Badge>
                          )}
                        </div>

                        {/* Time Flow */}
                        <div className="flex items-center gap-2 text-sm mb-2">
                          <div className="p-2 bg-gray-700 rounded">
                            <span className="text-gray-400">Depart {airportCode}:</span>
                            <span className="text-white font-bold ml-2">{formatTime(slot.departTime)}</span>
                          </div>
                          <span className="text-gray-500">→</span>
                          <div className="p-2 bg-gray-700 rounded">
                            <span className="text-gray-400">Arrive {airportCode}:</span>
                            <span className="text-white font-bold ml-2">{formatTime(slot.returnTime)}</span>
                          </div>
                        </div>

                        {/* Flight Details */}
                        <div className="text-sm text-gray-400 mb-2">
                          Flight time from {hubCode}: <span className="text-white">{slot.flightTimeFromHub} min</span>
                        </div>

                        {/* Warnings */}
                        <div className="space-y-1 mt-3">
                          <div className="text-sm text-amber-400">
                            ! Requires {hubCode} departure by <strong>{formatTime(slot.hubDepartureRequired)}</strong>
                          </div>
                          <div className="text-sm text-amber-400">
                            ! Aircraft returns {airportCode} {formatTime(slot.returnTime)} - back to {hubCode} by <strong>{formatTime(slot.hubReturnTime)}</strong>
                          </div>
                        </div>

                        {/* Schedule Conflict Check */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConflictCheck(prev => ({
                              ...prev,
                              [slot.id]: !prev[slot.id]
                            }));
                          }}
                        >
                          {showConflicts ? "Hide" : "Check"} Schedule Conflicts
                        </Button>

                        {/* Conflict Details */}
                        {showConflicts && (
                          <div className="mt-3 p-3 bg-gray-900 rounded-lg">
                            {conflicts.length === 0 ? (
                              <div className="text-green-400">
                                ✓ No scheduling conflicts detected
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {conflicts.map((conflict, i) => (
                                  <div key={i} className="text-red-400 text-sm">
                                    ✗ {conflict.message}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* ================================================================ */}
        {/* PLAYER'S CURRENT SCHEDULE */}
        {/* ================================================================ */}
        {playerSchedule.length > 0 && (
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-4">
              Your Current Daily Schedule
            </h3>

            <div className="bg-gray-800 rounded-lg p-3 max-h-40 overflow-y-auto">
              <div className="space-y-2">
                {playerSchedule.slice(0, 10).map((flight, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-2 bg-gray-700/50 rounded">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono">{flight.flightNumber}</Badge>
                      <span className="text-gray-300">
                        {flight.origin} → {flight.destination}
                      </span>
                    </div>
                    <div className="text-gray-400">
                      {formatTime(flight.departureTime)} - {formatTime(flight.arrivalTime)}
                    </div>
                  </div>
                ))}
              </div>
              {playerSchedule.length > 10 && (
                <p className="text-gray-500 text-xs mt-2">+ {playerSchedule.length - 10} more flights</p>
              )}
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* COMPETING AIRLINES */}
        {/* ================================================================ */}
        {competitors.length > 0 && (
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-4">
              Competing Airlines Also Interested
            </h3>

            <div className="flex flex-wrap gap-2">
              {competitors.map((competitor, i) => (
                <Badge key={i} variant="outline" className="text-orange-400 border-orange-400">
                  {typeof competitor === 'string' ? competitor : competitor.name}
                </Badge>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-2">
              Higher bids may be necessary to secure slots
            </p>
          </div>
        )}

        {/* ================================================================ */}
        {/* BID SECTION */}
        {/* ================================================================ */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-4">
            Place Your Bid
          </h3>

          {/* Quantity Selection */}
          <div className="mb-4">
            <label className="text-gray-300 text-sm mb-2 block">
              How many slots would you like to bid on?
            </label>
            <div className="flex gap-2">
              {[1, 2, 3].filter(n => n <= availableSlots.length).map((num) => (
                <Button
                  key={num}
                  variant={selectedQuantity === num ? "default" : "outline"}
                  className={selectedQuantity === num ? "bg-blue-600" : ""}
                  onClick={() => {
                    setSelectedQuantity(num);
                    setSelectedSlots([]); // Reset selections when quantity changes
                  }}
                >
                  {num}
                </Button>
              ))}
              <Button
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500/10"
                onClick={onDecline}
              >
                Decline
              </Button>
            </div>
          </div>

          {/* Slot Selection Prompt */}
          {selectedQuantity && selectedSlots.length < selectedQuantity && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-400 text-sm">
                Click on {selectedQuantity - selectedSlots.length} more slot{selectedQuantity - selectedSlots.length > 1 ? "s" : ""} above to select
              </p>
            </div>
          )}

          {/* Bid Amount */}
          {selectedQuantity && (
            <div className="mb-4">
              <label className="text-gray-300 text-sm mb-2 block">
                Your bid per slot (minimum $50,000):
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">$</span>
                <Input
                  type="number"
                  min={50000}
                  step={5000}
                  value={bidAmount}
                  onChange={(e) => setBidAmount(Math.max(50000, parseInt(e.target.value) || 50000))}
                  className="bg-gray-800 border-gray-600 text-white w-40"
                />
              </div>
              {bidAmount < 50000 && (
                <p className="text-red-400 text-xs mt-1">Minimum bid is $50,000</p>
              )}
            </div>
          )}

          {/* Total Cost Breakdown */}
          {totalCost && selectedQuantity && (
            <div className="bg-gray-800 rounded-lg p-4 mb-4">
              <h4 className="text-white font-medium mb-3">Total Cost Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Facility Fees ({selectedQuantity} slots)</span>
                  <span className="text-white">{formatCurrency(totalCost.facilityFee)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Bid Amount ({selectedQuantity} x {formatCurrency(bidAmount)})</span>
                  <span className="text-white">{formatCurrency(totalCost.bidAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">First Month Lease ({selectedQuantity} slots)</span>
                  <span className="text-white">{formatCurrency(totalCost.firstMonthLease)}</span>
                </div>
                {totalCost.monthlySavings > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Legal Discount Savings</span>
                    <span>-{formatCurrency(totalCost.monthlySavings)}/month</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-2 mt-2">
                  <div className="flex justify-between text-lg">
                    <span className="text-white font-bold">Total Due Now</span>
                    <span className={`font-bold ${canAfford ? "text-green-400" : "text-red-400"}`}>
                      {formatCurrency(totalCost.totalUpfront)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">Your Cash</span>
                    <span className={canAfford ? "text-gray-400" : "text-red-400"}>
                      {formatCurrency(playerCash)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* FOOTER ACTIONS */}
        {/* ================================================================ */}
        <div className="p-6 bg-gray-800/50 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={onGoBack}
          >
            ← Go Back
          </Button>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onDecline}
              className="border-gray-600 text-gray-400"
            >
              Decline Negotiation
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !selectedQuantity ||
                selectedSlots.length !== selectedQuantity ||
                bidAmount < 50000 ||
                !canAfford
              }
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500"
            >
              Submit Bid
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SLOT GENERATION HELPER
// ============================================================================

/**
 * Generate available slots for an airport
 * @param {string} airportCode - The airport offering slots
 * @param {string} hubCode - Player's hub airport
 * @param {number} flightTimeMinutes - Flight time between hub and airport
 * @param {number} count - Number of slots to generate
 */
export function generateAvailableSlots(airportCode, hubCode, flightTimeMinutes, count = 3) {
  const slots = [];

  // Typical slot windows - morning, midday, evening
  const slotWindows = [
    { departTime: "06:15", label: "A" },
    { departTime: "10:30", label: "B" },
    { departTime: "14:45", label: "C" },
    { departTime: "18:00", label: "D" },
  ];

  for (let i = 0; i < Math.min(count, slotWindows.length); i++) {
    const window = slotWindows[i];
    const [depHours, depMinutes] = window.departTime.split(":").map(Number);

    // Calculate hub departure required (flight time + 30 min buffer)
    const hubDepMinutes = depHours * 60 + depMinutes - flightTimeMinutes - 30;
    const hubDepHours = Math.floor(hubDepMinutes / 60);
    const hubDepMins = hubDepMinutes % 60;

    // Calculate return times
    // Assume turnaround time of 45 min + return flight
    const turnTime = 45;
    const returnMinutes = depHours * 60 + depMinutes + turnTime + flightTimeMinutes + flightTimeMinutes + turnTime;
    const returnHours = Math.floor((returnMinutes % (24 * 60)) / 60);
    const returnMins = returnMinutes % 60;

    // Hub return time
    const hubReturnMinutes = returnMinutes + flightTimeMinutes + 30;
    const hubReturnHours = Math.floor((hubReturnMinutes % (24 * 60)) / 60);
    const hubReturnMins = hubReturnMinutes % 60;

    const formatTimeStr = (h, m) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    slots.push({
      id: `${airportCode}-${window.label}`,
      departTime: window.departTime,
      returnTime: formatTimeStr(returnHours, returnMins),
      flightTimeFromHub: flightTimeMinutes,
      hubDepartureRequired: formatTimeStr(hubDepHours, hubDepMins),
      hubReturnTime: formatTimeStr(hubReturnHours, hubReturnMins),
    });
  }

  return slots;
}

/**
 * Generate competing airlines from actual RelationsEngine competitors
 * Per CLAUDE_PROTOCOL.md - these are the real AI airlines the player has relations with
 */
export function generateCompetitors(count = 3, airportCode = null, relationsState = null) {
  // Actual competitors from RelationsEngine with their aggression levels
  const ACTUAL_COMPETITORS = [
    { id: "NORTHREACH", name: "NorthReach Air", aggression: 6 },
    { id: "CASCADE", name: "Cascade Wings", aggression: 4 },
    { id: "COPPER_RIDGE", name: "Copper Ridge Air", aggression: 9 },
    { id: "HIGHPLAINS", name: "HighPlains Airways", aggression: 8 },
    { id: "UNION_SKIES", name: "Union Skies", aggression: 6 },
    { id: "ATLAS_CROWN", name: "Atlas Crown", aggression: 5 },
    { id: "SUNREACH", name: "SunReach Airlines", aggression: 7 },
    { id: "FRONTLINE", name: "Frontline Aero", aggression: 7 },
    { id: "IRONWAVE", name: "IronWave Air", aggression: 6 },
    { id: "POLAR_MERIDIAN", name: "Polar Meridian", aggression: 5 },
  ];

  // If we have relations state, prioritize competitors with presence at this airport
  let relevantCompetitors = [...ACTUAL_COMPETITORS];

  if (relationsState && airportCode) {
    const airportLoyalty = relationsState.airports?.[airportCode]?.loyalty || {};
    // Sort by loyalty (competitors with presence at this airport first)
    relevantCompetitors.sort((a, b) => {
      const loyaltyA = airportLoyalty[a.id] || 0;
      const loyaltyB = airportLoyalty[b.id] || 0;
      return loyaltyB - loyaltyA;
    });
  } else {
    // Shuffle if no relations context
    relevantCompetitors.sort(() => Math.random() - 0.5);
  }

  return relevantCompetitors.slice(0, count);
}
